import React, { useState, useEffect, useCallback, useContext } from "react";
import { Card, CardBody, Col, Container, Row } from "reactstrap";
import { useParams } from "react-router-dom";
import Breadcrumb from "../../components/Common/Breadcrumb";
import { getUser } from "../../services/ValitanApiService";
import avatar from "../../assets/images/users/td_logo.jpeg";
import { LoadingContext } from "../../../src/Pages/Utility/spinner/LoadingContext";

const BRAND_BLUE = "#0147C8";

const UserDetails = () => {
    const { userId } = useParams();
    const { setIsLoading } = useContext(LoadingContext);

    const [user, setUser] = useState(null);
    const [notFound, setNotFound] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [activeTab, setActiveTab] = useState("details");

    useEffect(() => {
        document.title = "User Details | Valitan";
    }, []);

    const normalize = useCallback(
        (u) => ({
            ...u,
            roles: (u.roles || []).map((r) => r.name),
            is_banned: u.is_banned === "Yes",
            created_at: u.created_at ? new Date(u.created_at).toLocaleString() : "-",
            // ✅ updated_at removed completely — not displayed
            is_valuer: u.is_valuer === true || u.is_valuer === "Yes",
            is_entity: u.is_entity === true || u.is_entity === "Yes",
        }),
        []
    );

    useEffect(() => {
        const fetchUser = async () => {
            try {
                setIsLoading(true);
                setNotFound(false);

                const response = await getUser(userId);
                if (response.status === 200 && response.data) {
                    setUser(normalize(response.data));
                } else {
                    setNotFound(true);
                    setErrorMessage("User not found!");
                }
            } catch (err) {
                setNotFound(true);
                setErrorMessage("User not found!");
            } finally {
                setIsLoading(false);
            }
        };
        fetchUser();
    }, [userId, normalize, setIsLoading]);

    /** ✅ Neutral styled info row */
    const InfoRow = ({ label, value }) => (
        <div
            style={{
                display: "flex",
                justifyContent: "space-between",
                padding: "10px 0",
                borderBottom: "1px solid #f0f0f0",
            }}
        >
            <span style={{ fontWeight: "600", color: "#444" }}>{label}</span>
            <span style={{ textAlign: "right", color: "#333" }}>{value || "-"}</span>
        </div>
    );

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <Breadcrumb title="Users management" breadcrumbItem="Details" />

                    {notFound ? (
                        <Row>
                            <Col lg="12">
                                <Card>
                                    <CardBody>
                                        <div className="text-center">
                                            <h5 className="text-danger">
                                                <em>{errorMessage}</em>
                                            </h5>
                                        </div>
                                    </CardBody>
                                </Card>
                            </Col>
                        </Row>
                    ) : (
                        user && (
                            <Row>
                                {/* ✅ LEFT SIDE PROFILE CARD */}
                                <Col lg="4">
                                    <Card>
                                        <CardBody>
                                            <div className="text-center">
                                                <img
                                                    src={avatar}
                                                    alt="User Avatar"
                                                    className="avatar-lg rounded-circle img-thumbnail mx-auto d-block"
                                                    style={{ marginBottom: "16px" }}
                                                />
                                            </div>

                                            <hr
                                                style={{
                                                    width: "90%",
                                                    margin: "20px auto",
                                                    border: 0,
                                                    height: "1px",
                                                    backgroundImage:
                                                        "linear-gradient(to right, rgba(0,0,0,0), #999, rgba(0,0,0,0))",
                                                }}
                                            />

                                            <InfoRow label="First Name" value={user.first_name} />
                                            <InfoRow label="Middle Name" value={user.middle_name} />
                                            <InfoRow label="Last Name" value={user.last_name} />
                                            <InfoRow label="Sex" value={user.sex} />

                                            {/* ✅ Roles */}
                                            <div
                                                className="text-center text-white text-uppercase mt-3"
                                                style={{
                                                    backgroundColor: BRAND_BLUE,
                                                    padding: "8px",
                                                    borderRadius: "4px",
                                                    fontWeight: "600",
                                                }}
                                            >
                                                {user.roles.length
                                                    ? user.roles.join(" • ")
                                                    : "NO ROLE"}
                                            </div>
                                        </CardBody>
                                    </Card>
                                </Col>

                                {/* ✅ RIGHT SIDE CONTENT */}
                                <Col lg="8" className="ms-auto">
                                    <div className="card">
                                        <div className="card-body">
                                            {/* ✅ Tabs */}
                                            <ul className="navtab-bg nav-justified nav nav-pills">
                                                {["details", "profile", "messages", "settings"].map((tab) => (
                                                    <li className="nav-item" key={tab}>
                                                        <button
                                                            className={`nav-link ${activeTab === tab ? "active" : ""
                                                                }`}
                                                            onClick={() => setActiveTab(tab)}
                                                            style={{
                                                                cursor: "pointer",
                                                                color:
                                                                    activeTab === tab
                                                                        ? "#fff"
                                                                        : BRAND_BLUE,
                                                                backgroundColor:
                                                                    activeTab === tab
                                                                        ? BRAND_BLUE
                                                                        : "transparent",
                                                            }}
                                                        >
                                                            {tab === "details" && (
                                                                <>
                                                                    <i className="dripicons-information me-1"></i>
                                                                    Details
                                                                </>
                                                            )}
                                                            {tab === "profile" && (
                                                                <>
                                                                    <i className="dripicons-user me-1"></i>
                                                                    Profile
                                                                </>
                                                            )}
                                                            {tab === "messages" && (
                                                                <>
                                                                    <i className="dripicons-mail me-1"></i>
                                                                    Messages
                                                                </>
                                                            )}
                                                            {tab === "settings" && (
                                                                <>
                                                                    <i className="dripicons-gear me-1"></i>
                                                                    Settings
                                                                </>
                                                            )}
                                                        </button>
                                                    </li>
                                                ))}
                                            </ul>

                                            {/* ✅ TAB CONTENT */}
                                            <div className="tab-content p-3 text-muted">
                                                {activeTab === "details" && (
                                                    <div>
                                                        <InfoRow label="Email" value={user.email} />
                                                        <InfoRow label="Mobile Number" value={user.mobile_number} />
                                                        <InfoRow label="Username" value={user.username} />
                                                        <InfoRow label="Address" value={user.address} />
                                                        <InfoRow label="NIN" value={user.nin} />

                                                        {/* ✅ Always show Is Valuer */}
                                                        <InfoRow
                                                            label="Is Valuer?"
                                                            value={user.is_valuer ? "Yes" : "No"}
                                                        />

                                                        {/* ✅ SHOW certificate only if valuer == yes */}
                                                        {user.is_valuer && (
                                                            <InfoRow
                                                                label="Certificate Number"
                                                                value={user.certificate_number}
                                                            />
                                                        )}

                                                        {/* ✅ Company block only if is_entity = true */}
                                                        {user.is_entity && (
                                                            <>
                                                                <InfoRow
                                                                    label="Is Company?"
                                                                    value={user.is_entity ? "Yes" : "No"}
                                                                />
                                                                <InfoRow label="Company" value={user.company} />
                                                                <InfoRow label="TIN" value={user.tin} />
                                                            </>
                                                        )}

                                                        {/* ✅ Always show Created At */}
                                                        <InfoRow label="Created At" value={user.created_at} />

                                                        {/* ❌ Updated At — intentionally removed */}
                                                    </div>
                                                )}

                                                {activeTab === "profile" && <p>Profile content…</p>}
                                                {activeTab === "messages" && <p>Messages content…</p>}
                                                {activeTab === "settings" && <p>Settings content…</p>}
                                            </div>
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                        )
                    )}
                </Container>
            </div>
        </React.Fragment>
    );
};

export default UserDetails;
